import { createClient } from "@supabase/supabase-js";
import { Database } from "@/integrations/supabase/types";
import {
  isSafari,
  isLocalStorageAvailable,
  isSessionStorageAvailable,
} from "@/lib/utils/browser-detection";
import { supabaseAuthStorage } from "@/lib/utils/remember-me";

// Environment variables - Mode développement tolérant pour tester l'UI
// En production, les variables sont obligatoires pour la sécurité
const useProdDataInLocal =
  import.meta.env.DEV && import.meta.env.VITE_USE_PROD_DATA_IN_LOCAL === "true";

const resolvedSupabaseUrl = useProdDataInLocal
  ? import.meta.env.VITE_PROD_SUPABASE_URL
  : import.meta.env.VITE_SUPABASE_URL;
const resolvedSupabaseKey = useProdDataInLocal
  ? import.meta.env.VITE_PROD_SUPABASE_ANON_KEY
  : import.meta.env.VITE_SUPABASE_ANON_KEY;

const supabaseUrl =
  resolvedSupabaseUrl || (import.meta.env.DEV ? "https://ui-mode-placeholder.supabase.co" : undefined);
const supabaseKey = resolvedSupabaseKey || (import.meta.env.DEV ? "ui-mode-placeholder-key" : undefined);

const isSupabaseConfigured = Boolean(supabaseUrl && supabaseKey && !supabaseUrl.includes("placeholder"));
if (import.meta.env.DEV) {
  if (isSupabaseConfigured) {
    if (useProdDataInLocal) {
      console.log("[Supabase] Local connecté aux données PROD (lecture locale activée)");
    } else {
      console.log("[Supabase] Configuré pour le développement local (auth et données actives)");
    }
  } else {
    console.warn("[Supabase] Non configuré — ajoutez VITE_SUPABASE_URL et VITE_SUPABASE_ANON_KEY dans .env ou .env.local puis redémarrez le serveur (npm run dev)");
  }
}

// En mode développement, on permet l'exécution sans variables pour tester l'UI
if (!import.meta.env.VITE_SUPABASE_URL && import.meta.env.DEV) {
  console.warn('⚠️ VITE_SUPABASE_URL manquant - Mode UI uniquement activé');
  console.warn('📝 Les fonctionnalités backend (auth, base de données) ne fonctionneront pas');
  console.warn('📝 Pour activer toutes les fonctionnalités, crée un fichier .env avec:');
  console.warn('   VITE_SUPABASE_URL=your_supabase_url');
}

if (!import.meta.env.VITE_SUPABASE_ANON_KEY && import.meta.env.DEV) {
  console.warn('⚠️ VITE_SUPABASE_ANON_KEY manquant - Mode UI uniquement activé');
  console.warn('📝 Ajoute à ton fichier .env:');
  console.warn('   VITE_SUPABASE_ANON_KEY=your_supabase_anon_key');
}

// En production, on garde les erreurs strictes pour la sécurité
if (!import.meta.env.DEV) {
  if (!import.meta.env.VITE_SUPABASE_URL) {
    throw new Error("Missing VITE_SUPABASE_URL environment variable. Please check your Vercel environment variables.");
  }
  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    throw new Error("Missing VITE_SUPABASE_ANON_KEY environment variable. Please check your Vercel environment variables.");
  }
}

// Configuration robuste du client Supabase avec des options améliorées
// Safari-specific optimizations
const isSafariBrowser = isSafari();
const storageAvailable = isLocalStorageAvailable();

export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: storageAvailable, // Only persist if localStorage is available
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Safari-specific: Use longer timeout for session recovery
    storage: storageAvailable ? supabaseAuthStorage : undefined,
    storageKey: "supabase.auth.token",
    // Increase timeout for Safari which can be slower
    flowType: "pkce", // Use PKCE flow which is more reliable in Safari
  },
  global: {
    headers: {
      apikey: supabaseKey,
      // IMPORTANT : Ne pas définir Content-Type ici car il doit être défini dynamiquement :
      // - Pour les requêtes REST API (JSON), Supabase le définit automatiquement
      // - Pour les uploads de fichiers Storage, le Content-Type doit être le MIME type du fichier
      //   (passé via l'option contentType dans .upload())
      Accept: "application/json",
    },
  },
  db: {
    schema: "public",
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: unknown) => {
  if (error instanceof Error) {
    console.error("Supabase error:", error.message);
    return error.message;
  }
  console.error("Unknown error:", error);
  return "Une erreur inattendue est survenue";
};

// Types for common Supabase responses
export type SupabaseResponse<T> = {
  data: T | null;
  error: Error | null;
};

// Utility function to check if user is authenticated
export const isAuthenticated = async () => {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();
  if (error) {
    console.error("Auth check error:", error.message);
    return false;
  }
  return !!session;
};

// Utility function to get current user
export const getCurrentUser = async () => {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error) {
    console.error("Get user error:", error.message);
    return null;
  }
  return user;
};

// Fonction pour effacer complètement la session et la recréer
export const clearAuthSession = async () => {
  try {
    // Se déconnecter proprement
    await supabase.auth.signOut({ scope: "local" });

    // Supprimer les données d'authentification du localStorage
    if (isLocalStorageAvailable()) {
      window.localStorage.removeItem("supabase.auth.token");

      // Autres clés potentielles
      const keys = Object.keys(window.localStorage);
      keys.forEach((key) => {
        if (key.includes("supabase") || key.includes("auth") || key.includes("rakeb")) {
          window.localStorage.removeItem(key);
        }
      });
    }

    // Also clear sessionStorage as fallback
    if (isSessionStorageAvailable()) {
      const sessionKeys = Object.keys(window.sessionStorage);
      sessionKeys.forEach((key) => {
        if (key.includes("supabase") || key.includes("auth") || key.includes("rakeb")) {
          window.sessionStorage.removeItem(key);
        }
      });
    }

    console.log("Auth session cleared successfully");
    return true;
  } catch (err) {
    console.error("Failed to clear auth session:", err);
    return false;
  }
};

/**
 * Get session with Safari-specific retries and timeouts
 */
export const getSessionWithRetry = async (maxRetries = 3, delay = 500): Promise<any> => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const { data, error } = await supabase.auth.getSession();
      if (!error && data.session) {
        return data.session;
      }

      // If Safari and no session, wait a bit longer before retry
      if (isSafariBrowser && i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
      }
    } catch (err) {
      console.error(`Session retrieval attempt ${i + 1} failed:`, err);
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }
  return null;
};
